import React, { useRef } from 'react';
import { useField } from 'formik';
import { Upload, X, File } from 'lucide-react';
import { toast } from 'react-toastify';
import styles from './FormikFileUpload.module.css';

interface FormikFileUploadProps {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  maxFiles?: number;
  allowedTypes?: string[]; // e.g., ['pdf', 'png', 'jpeg']
}

// Magic bytes (file signatures) for file type validation
const FILE_SIGNATURES: { [key: string]: { signature: string | number[]; offset: number } } = {
  pdf: { signature: [0x25, 0x50, 0x44, 0x46], offset: 0 }, // %PDF
  png: { signature: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], offset: 0 }, // PNG signature
  jpeg: { signature: [0xff, 0xd8, 0xff], offset: 0 }, // JPEG signature
  jpg: { signature: [0xff, 0xd8, 0xff], offset: 0 }, // Same as JPEG
};

// Read file signature (magic bytes)
const readFileSignature = (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target?.result instanceof ArrayBuffer) {
        const bytes = new Uint8Array(e.target.result.slice(0, 16)); // Read first 16 bytes
        resolve(bytes);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(0, 16));
  });
};

// Validate file type using magic bytes
const validateFileType = async (file: File, allowedTypes: string[]): Promise<boolean> => {
  try {
    const bytes = await readFileSignature(file);
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

    for (const allowedType of allowedTypes) {
      const type = allowedType.toLowerCase();
      const signature = FILE_SIGNATURES[type];

      if (signature) {
        const { signature: expectedSig, offset } = signature;
        const matches = Array.isArray(expectedSig)
          ? expectedSig.every((byte, index) => bytes[offset + index] === byte)
          : false;

        if (matches || fileExtension === type || fileExtension === 'jpg') {
          return true;
        }
      }
    }

    return false;
  } catch (error) {
    console.error('Error validating file type:', error);
    return false;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const FormikFileUpload: React.FC<FormikFileUploadProps> = ({
  name,
  label,
  placeholder = 'Dosya seçin veya buraya sürükleyin',
  className,
  accept,
  multiple = true,
  maxSize = 2, // Default 2MB
  maxFiles = 3, // Default 3 files
  allowedTypes = ['pdf', 'png', 'jpeg', 'jpg'],
}) => {
  const [field, meta, helpers] = useField<File[]>(name);
  const [isValidating, setIsValidating] = React.useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const files: File[] = field.value || [];

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    // Check file count limit
    if (maxFiles && files.length + selectedFiles.length > maxFiles) {
      toast.error(`En fazla ${maxFiles} dosya yükleyebilirsiniz.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check file size
    const maxSizeBytes = maxSize * 1024 * 1024; // Convert MB to bytes
    const oversizedFiles = selectedFiles.filter(file => file.size > maxSizeBytes);
    if (oversizedFiles.length > 0) {
      toast.error(`Bazı dosyalar maksimum boyutu (${maxSize}MB) aşıyor.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file types with magic bytes
    setIsValidating(true);
    const validFiles: File[] = [];

    for (const file of selectedFiles) {
      const isValid = await validateFileType(file, allowedTypes);
      if (isValid) {
        validFiles.push(file);
      } else {
        toast.error(
          `${file.name} desteklenmeyen dosya formatı. Sadece PDF, PNG ve JPEG dosyaları kabul edilir.`
        );
      }
    }

    setIsValidating(false);

    if (validFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check file count limit again after validation
    if (maxFiles && files.length + validFiles.length > maxFiles) {
      toast.error(`En fazla ${maxFiles} dosya yükleyebilirsiniz.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const newFiles = multiple ? [...files, ...validFiles] : validFiles;
    helpers.setValue(newFiles);

    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    helpers.setValue(newFiles);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);

    // Check file count limit
    if (maxFiles && files.length + droppedFiles.length > maxFiles) {
      toast.error(`En fazla ${maxFiles} dosya yükleyebilirsiniz.`);
      return;
    }

    // Check file size
    const maxSizeBytes = maxSize * 1024 * 1024;
    const oversizedFiles = droppedFiles.filter(file => file.size > maxSizeBytes);
    if (oversizedFiles.length > 0) {
      toast.error(`Bazı dosyalar maksimum boyutu (${maxSize}MB) aşıyor.`);
      return;
    }

    // Validate file types with magic bytes
    setIsValidating(true);
    const validFiles: File[] = [];

    for (const file of droppedFiles) {
      const isValid = await validateFileType(file, allowedTypes);
      if (isValid) {
        validFiles.push(file);
      } else {
        toast.error(
          `${file.name} desteklenmeyen dosya formatı. Sadece PDF, PNG ve JPEG dosyaları kabul edilir.`
        );
      }
    }

    setIsValidating(false);

    if (validFiles.length === 0) {
      return;
    }

    // Check file count limit again after validation
    if (maxFiles && files.length + validFiles.length > maxFiles) {
      toast.error(`En fazla ${maxFiles} dosya yükleyebilirsiniz.`);
      return;
    }

    const newFiles = multiple ? [...files, ...validFiles] : validFiles;
    helpers.setValue(newFiles);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`${styles.container} ${className || ''}`}>
      {label && <label className={styles.label}>{label}</label>}

      <div
        className={`${styles.dropZone} ${meta.touched && meta.error ? styles.error : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          className={styles.fileInput}
        />

        <div className={styles.dropZoneContent}>
          <Upload size={24} className={styles.uploadIcon} />
          <span className={styles.placeholder}>
            {isValidating ? 'Dosyalar kontrol ediliyor...' : placeholder}
          </span>
          <span className={styles.fileLimitInfo}>
            (Maksimum {maxFiles} dosya, her biri en fazla {maxSize}MB, PDF/PNG/JPEG)
          </span>
        </div>
      </div>

      {files.length > 0 && (
        <div className={styles.fileList}>
          {files.map((file, index) => (
            <div key={index} className={styles.fileItem}>
              <File size={16} className={styles.fileIcon} />
              <span className={styles.fileName} title={file.name}>
                {file.name}
              </span>
              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
              <button
                type="button"
                className={styles.removeButton}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveFile(index);
                }}
                title="Kaldır"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {meta.touched && meta.error && <div className={styles.errorMessage}>{meta.error}</div>}
    </div>
  );
};
